import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { AccountService, ContentService, ContextService } from '@core/services';
import { IContentBlock } from '@core/services/interfaces';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { loadMe } from '@store/actions/profile.actions';
import { getMe } from '@store/reducers/profile.reducer';
import {
  Content,
  CSchool,
  ESchoolParticipantRole,
  FindContentStrategyEnum,
  IFindContentFilter,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil, tap } from 'rxjs/operators';

type ContentBlock = IContentBlock<Content, IFindContentFilter>;

@Component({
  selector: 'app-school-feed',
  templateUrl: './school-feed.component.html',
  styleUrls: ['./school-feed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolFeedComponent extends ChangableComponent implements OnInit {
  // DI
  public deviceService = inject(DeviceDetectorService);
  private store = inject(Store);
  private router = inject(Router);
  protected contentService = inject(ContentService);
  protected contextService = inject(ContextService);
  protected loadingBar = inject(LoadingBarService);

  @Input()
  public set school(school: Partial<CSchool>) {
    this._school = school;
    this.content.filter.school_id = this._school.id;
    this.loadContent();
  }

  public get school(): Partial<CSchool> {
    return this._school;
  }

  @Output() public loaded = new EventEmitter<boolean>();

  private _me: User;
  private _school: Partial<CSchool>;
  public isTeacher: boolean;
  public pathName: string;
  public content: ContentBlock = {
    items: [],
    loading: false,
    loaded: false,
    filter: {
      use: FindContentStrategyEnum.school,
    },
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
  };

  public debug$ = this.contextService.debug$;
  public dbgContentId = '';

  ngOnInit() {
    this.pathName = this.router.url.split('/')[3];
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          this.pathName = this.router.url.split('/')[3];
          this.markForCheck();
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.isTeacher = AccountService.isTeacher(me);
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });
  }

  protected loadContent(nextPage?: boolean) {
    if (this.content.loading) {
      return;
    }

    this.content.loading = true;
    this.loadingBar.fetchingStart(35);
    if (nextPage && this.content.pagination.page < this.content.pagination.totalPages) {
      this.content.pagination.page = this.content.pagination.page + 1;
    }

    this.contentService
      .getContent(this.content.filter, this.content.pagination, false)
      .pipe(tap())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (nextPage) {
            this.content.items = [...this.content.items, ...response.results];
          } else {
            this.content.items = response.results;
          }
          this.content.pagination = response.pagination;
          this.content.loading = false;
          this.content.loaded = true;
          this.loaded.emit(true);
          this.loadingBar.fetchingComplete();
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.content.loaded = true;
          this.loaded.emit(true);
        },
      });
  }

  public get meIsOwnerSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this._me?.id);
  }

  public trackContentByFn(item: Content): string {
    return item.id;
  }

  public noPublicationsMessage(): string {
    return this.meIsOwnerSchool
      ? 'classrooms.classroom-page.content.stubs.description'
      : 'classrooms.classroom-page.content.stubs.description-student';
  }

  public onContentPosted() {
    this.reloadContent();
  }

  public reloadContent() {
    this.content.pagination.page = 1;
    this.loadContent();
  }

  public nextPage() {
    this.loadContent(true);
  }

  /** Отладочный метод - добавляет в ленту публикаций школы контент, указанный в отладочном поле ввода */
  public dbgPostToSchool() {
    this.addToSchoolContent(this.dbgContentId);
  }

  /** Опубликовать урок (контент) в ленту школы */
  protected addToSchoolContent(content_id: string) {
    this.contentService
      .addToSchoolContent(this.school.id, content_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.reloadContent(),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.reloadContent();
        },
      });
  }
}
